import { lazy, Suspense, useEffect, useState } from "react";
import type { ReactNode } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";

import { PowerBESSLogo } from "@/components/PowerBESSLogo";
import { PageSkeleton } from "@/components/PageSkeleton";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { FadeUp, PageTransition } from "@/app/console/motion";
import { ActionButton } from "@/app/console/ui";

// Console surface (product-first routes at the root). Wraps urql + AuthProvider + ErrorBoundary.
import { AppShell } from "@/app/AppShell";
import { AppProviders } from "@/lib/AppProviders";
import { RequireAuth, RequireMfaEnrolled } from "@/lib/auth/RequireRole";

// ---------------------------------------------------------------------------
// Eager imports — small pages that are needed immediately (login, landing).
// ---------------------------------------------------------------------------
import LoginPage from "@/app/pages/Login";
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from "@/app/pages/auth/RecoverPages";

// ---------------------------------------------------------------------------
// Lazy imports — heavy screens loaded on demand so the initial bundle is small.
// Vite will code-split each of these into its own chunk automatically.
// manualChunks in vite.config.ts groups recharts + radix into separate vendor
// chunks to improve long-term caching.
// ---------------------------------------------------------------------------

const AppDashboard  = lazy(() => import("@/app/pages/Dashboard"));
const Overview      = lazy(() => import("@/app/pages/Overview"));
const AssetsList    = lazy(() => import("@/app/pages/AssetsList"));
const AssetDetail   = lazy(() => import("@/app/pages/AssetDetail"));
const Market        = lazy(() => import("@/app/pages/Market"));
const Forecasts     = lazy(() => import("@/app/pages/forecasts/index"));
const Backtests     = lazy(() => import("@/app/pages/Backtests"));
const Analytics     = lazy(() => import("@/app/pages/Analytics"));
const AlertsPage    = lazy(() => import("@/app/pages/Alerts"));
const AdminTenants  = lazy(() => import("@/app/pages/AdminTenants"));
const TenantUsers   = lazy(() => import("@/app/pages/TenantUsers"));
const AdminAudit    = lazy(() => import("@/app/pages/AdminAudit"));
const SettingsPage  = lazy(() => import("@/app/pages/Settings"));

// Product overviews (Comply, Data)
const ComplyOverview = lazy(() => import("@/app/pages/comply/ComplyOverview"));
const DataOverview   = lazy(() => import("@/app/pages/data/DataOverview"));

// Comply product pages
const DsmAssessments  = lazy(() => import("@/app/pages/comply/DsmAssessments"));
const Reconciliation  = lazy(() => import("@/app/pages/comply/Reconciliation"));
const AuditChain      = lazy(() => import("@/app/pages/comply/AuditChain"));
const Scheduling      = lazy(() => import("@/app/pages/comply/Scheduling"));
const RegulatorExport = lazy(() => import("@/app/pages/comply/RegulatorExport"));

// Health product pages
const Telemetry   = lazy(() => import("@/app/pages/health/Telemetry"));
const Connectors  = lazy(() => import("@/app/pages/health/Connectors"));
const Degradation = lazy(() => import("@/app/pages/health/Degradation"));
const DataTrust   = lazy(() => import("@/app/pages/health/DataTrust"));
const AssetWorkspace = lazy(() => import("@/app/pages/asset/Workspace"));
const AssetTickets = lazy(() => import("@/app/pages/asset/Tickets"));

// Data product pages
const MarketData = lazy(() => import("@/app/pages/data/MarketData"));
const GridData   = lazy(() => import("@/app/pages/data/GridData"));
const Settlement = lazy(() => import("@/app/pages/data/Settlement"));
const Exports    = lazy(() => import("@/app/pages/data/Exports"));

// Organization / Manage area
const OrgOverview   = lazy(() => import("@/app/pages/org/OrgOverview"));
const OrgMembers    = lazy(() => import("@/app/pages/org/OrgMembers"));
const OrgRoles      = lazy(() => import("@/app/pages/org/OrgRoles"));

// ---------------------------------------------------------------------------
// Legal pages — console tokens (no legacy .legal-* / hex styles); same
// structure as before: header with wordmark + Back, title + date, sections.
// ---------------------------------------------------------------------------

function LegalPage({ title, testid, children }: { title: string; testid: string; children: ReactNode }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-surface-0 text-text-1" data-testid={testid}>
      <Helmet>
        <title>{title} | PowerBESS.ai</title>
      </Helmet>

      <header className="sticky top-0 z-sticky border-b border-line bg-surface-0">
        <div className="mx-auto flex h-header max-w-[800px] items-center justify-between px-5">
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
            <PowerBESSLogo size={28} />
            <span className="text-lg font-semibold">
              Power<span className="text-brand">BESS</span>.ai
            </span>
          </Link>
          <ActionButton variant="outline" size="sm" icon={ArrowLeft} onClick={() => navigate("/")}>
            Back
          </ActionButton>
        </div>
      </header>

      <main className="mx-auto max-w-[800px] px-5 py-12 sm:py-16">
        <FadeUp>
          <div className="mb-10 border-b border-line pb-8">
            <h1 className="text-[32px] font-semibold leading-10 tracking-tight">{title}</h1>
            <p className="mt-2 text-sm text-text-3">Last updated: January 2026</p>
          </div>
          <div className="space-y-10">{children}</div>
        </FadeUp>
      </main>
    </div>
  );
}

function LegalSection({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-lg font-semibold leading-[26px]">{heading}</h2>
      <p className="mt-3 text-sm leading-[22px] text-text-2">{children}</p>
    </section>
  );
}

const legalLink = "text-text-1 underline decoration-text-4 underline-offset-4 transition-colors duration-fast hover:decoration-text-1";

const PrivacyPolicy = () => (
  <LegalPage title="Privacy Policy" testid="privacy-page">
    <LegalSection heading="1. Information We Collect">
      At PowerBESS.ai, we collect information to provide better services. This includes account
      information, BESS configuration data, trading data, and usage data.
    </LegalSection>
    <LegalSection heading="2. How We Use Your Information">
      We use collected information to provide AI-powered arbitrage recommendations, generate
      optimized schedules, improve our platform, and send important notifications.
    </LegalSection>
    <LegalSection heading="3. Contact Us">
      For privacy inquiries:{" "}
      <a href="mailto:contact@powerbess.ai" className={legalLink}>
        contact@powerbess.ai
      </a>
    </LegalSection>
  </LegalPage>
);

const TermsOfService = () => (
  <LegalPage title="Terms of Service" testid="terms-page">
    <LegalSection heading="1. Acceptance of Terms">By accessing PowerBESS.ai, you agree to these Terms of Service.</LegalSection>
    <LegalSection heading="2. Description of Service">
      PowerBESS.ai provides AI-powered BESS arbitrage tools, IEX market analysis, charge/discharge
      scheduling, and revenue analytics. Our recommendations are advisory in nature.
    </LegalSection>
    <LegalSection heading="3. Contact">
      For questions:{" "}
      <a href="mailto:contact@powerbess.ai" className={legalLink}>
        contact@powerbess.ai
      </a>
    </LegalSection>
  </LegalPage>
);

// ---------------------------------------------------------------------------
// Suspense wrapper — provides loading skeleton while lazy chunks download.
// ---------------------------------------------------------------------------

function LazyRoute({ children }: { children: ReactNode }) {
  // PageTransition sits *inside* Suspense so the fade plays on the resolved
  // page, not the skeleton. A fresh LazyRoute mounts per navigation, so the
  // entrance fires exactly once per route change — tied to content. The
  // skeleton is delayed: chunks that are already cached (hover / idle preload,
  // see app/routePreload.ts) resolve before it ever appears, so there is no
  // flash — and when it does appear it fades in instead of popping.
  return (
    <Suspense fallback={<DelayedSkeleton />}>
      <PageTransition>{children}</PageTransition>
    </Suspense>
  );
}

const SKELETON_DELAY_MS = 180;

function DelayedSkeleton() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setShow(true), SKELETON_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);
  if (!show) return null;
  return (
    <div className="pb-skeleton-enter">
      <PageSkeleton />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legacy URL layer — the console used to live under /app/*. Every old path is
// permanently redirected (client-side, `replace`) to its product-first home,
// keeping the query string and hash so deep links and ?enrol=1 / #security
// style anchors keep working. Unknown /app/x falls back to the launcher.
// ---------------------------------------------------------------------------

/** Old /app/<rest> → new product-first path. */
function legacyPath(rest: string): string {
  const segs = rest.split("/").filter(Boolean);
  const head = segs[0];
  const tail = segs.length > 1 ? `/${segs.slice(1).join("/")}` : "";
  if (!head) return "/trade";
  switch (head) {
    case "overview":
      return `/overview${tail}`;
    // Trade — the old root was the trading desk; its pages move under /trade.
    case "market":
    case "forecasts":
    case "backtests":
    case "analytics":
      return `/trade/${head}${tail}`;
    case "model-backtesting":
      return "/trade/backtests";
    // Asset — fleet registry and alerts now live under the Asset product.
    case "assets":
      return `/asset/sites${tail}`;
    case "alerts":
      return `/asset/alerts${tail}`;
    case "asset":
    case "health":
    case "comply":
    case "data":
    case "org":
    case "admin":
    case "settings":
      return `/${head}${tail}`;
    // Platform admin — tenant user management sits under /admin/tenants.
    case "tenants":
      return `/admin/tenants${tail}`;
    default:
      return "/overview";
  }
}

function LegacyRedirect({ to }: { to?: string }) {
  const { search, hash } = useLocation();
  const params = useParams();
  const pathname = to ?? legacyPath(params["*"] ?? "");
  return <Navigate to={{ pathname, search, hash }} replace />;
}

// ---------------------------------------------------------------------------
// Route table
// ---------------------------------------------------------------------------

function AppRoutes() {
  return (
    <Routes>
      {/* The app's front door is sign-in; the marketing site is powerbess.ai. */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* /login talks to the real auth-service via the F1 AuthProvider.
          The whole console subtree shares one AppProviders root so the urql
          client + AuthProvider + ErrorBoundary are mounted exactly once. */}
      <Route
        path="/login"
        element={
          <AppProviders>
            <LoginPage />
          </AppProviders>
        }
      />

      {/* Logged-out, token-authenticated account flows — landing targets for the
          auth-service emails. No AuthProvider/urql needed (authClient is a plain
          fetch wrapper), so they mount bare. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Console — product-first routes at the root, one shell for all of them. */}
      <Route
        element={
          <AppProviders>
            <RequireAuth>
              <RequireMfaEnrolled>
                <AppShell />
              </RequireMfaEnrolled>
            </RequireAuth>
          </AppProviders>
        }
      >
        {/* Overview — launcher and post-login landing */}
        <Route path="/overview" element={<LazyRoute><Overview /></LazyRoute>} />

        {/* Trade */}
        <Route path="/trade" element={<LazyRoute><AppDashboard /></LazyRoute>} />
        <Route path="/trade/market" element={<LazyRoute><Market /></LazyRoute>} />
        <Route path="/trade/forecasts" element={<LazyRoute><Forecasts /></LazyRoute>} />
        <Route path="/trade/backtests" element={<LazyRoute><Backtests /></LazyRoute>} />
        <Route path="/trade/analytics" element={<LazyRoute><Analytics /></LazyRoute>} />

        {/* Asset — control-room workspace (views share one component), tickets,
            alerts and the fleet registry */}
        <Route path="/asset" element={<LazyRoute><AssetWorkspace /></LazyRoute>} />
        <Route path="/asset/tickets" element={<LazyRoute><AssetTickets /></LazyRoute>} />
        <Route path="/asset/alerts" element={<LazyRoute><AlertsPage /></LazyRoute>} />
        <Route path="/asset/sites" element={<LazyRoute><AssetsList /></LazyRoute>} />
        <Route path="/asset/sites/:id" element={<LazyRoute><AssetDetail /></LazyRoute>} />
        <Route path="/asset/:view" element={<LazyRoute><AssetWorkspace /></LazyRoute>} />

        {/* Health */}
        <Route path="/health" element={<Navigate to="/health/telemetry" replace />} />
        <Route path="/health/telemetry" element={<LazyRoute><Telemetry /></LazyRoute>} />
        <Route path="/health/connectors" element={<LazyRoute><Connectors /></LazyRoute>} />
        <Route path="/health/degradation" element={<LazyRoute><Degradation /></LazyRoute>} />
        <Route path="/health/battery" element={<LazyRoute><AssetWorkspace product="health" view="battery" /></LazyRoute>} />
        {/* Data trust is a real page; the workspace "trust" preset stays only as a registry entry. */}
        <Route path="/health/trust" element={<LazyRoute><DataTrust /></LazyRoute>} />
        {/* Health analytics folded into the Asset control room. */}
        <Route path="/health/analytics" element={<Navigate to="/asset" replace />} />

        {/* Comply */}
        <Route path="/comply" element={<LazyRoute><ComplyOverview /></LazyRoute>} />
        <Route path="/comply/assessments" element={<LazyRoute><DsmAssessments /></LazyRoute>} />
        <Route path="/comply/reconciliation" element={<LazyRoute><Reconciliation /></LazyRoute>} />
        <Route path="/comply/audit-chain" element={<LazyRoute><AuditChain /></LazyRoute>} />
        <Route path="/comply/scheduling" element={<LazyRoute><Scheduling /></LazyRoute>} />
        <Route path="/comply/export" element={<LazyRoute><RegulatorExport /></LazyRoute>} />

        {/* Data */}
        <Route path="/data" element={<LazyRoute><DataOverview /></LazyRoute>} />
        <Route path="/data/market" element={<LazyRoute><MarketData /></LazyRoute>} />
        <Route path="/data/grid" element={<LazyRoute><GridData /></LazyRoute>} />
        <Route path="/data/settlement" element={<LazyRoute><Settlement /></LazyRoute>} />
        <Route path="/data/exports" element={<LazyRoute><Exports /></LazyRoute>} />

        {/* Organisation */}
        <Route path="/org" element={<LazyRoute><OrgOverview /></LazyRoute>} />
        <Route path="/org/members" element={<LazyRoute><OrgMembers /></LazyRoute>} />
        <Route path="/org/roles" element={<LazyRoute><OrgRoles /></LazyRoute>} />
        <Route path="/org/audit" element={<LazyRoute><AdminAudit /></LazyRoute>} />

        {/* Account + platform admin */}
        <Route path="/settings" element={<LazyRoute><SettingsPage /></LazyRoute>} />
        <Route path="/admin" element={<Navigate to="/admin/tenants" replace />} />
        <Route path="/admin/tenants" element={<LazyRoute><AdminTenants /></LazyRoute>} />
        <Route path="/admin/tenants/:tid/users" element={<LazyRoute><TenantUsers /></LazyRoute>} />
        <Route path="/admin/audit" element={<LazyRoute><AdminAudit /></LazyRoute>} />
      </Route>

      {/* Legacy URL layer — see legacyPath(). Lives outside the auth wrapper so
          a signed-out visit to /app/forecasts bounces to /login with the NEW
          path as its return target. */}
      <Route path="/app/*" element={<LegacyRedirect />} />
      <Route path="/dashboard" element={<LegacyRedirect to="/trade" />} />

      {/* Catch-all: redirect unknown routes to landing. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ---------------------------------------------------------------------------
// App root
// ---------------------------------------------------------------------------

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <div className="App">
          <ImpersonationBanner />
          <AppRoutes />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
